import { createApi } from '@reduxjs/toolkit/query/react';
import { endPoints, urlKeys } from 'src/api/APIEndpoints';
import { formatString } from 'src/util/STR';
import { HTTPValidationError, ICreateQuery, ISuccessfulResponse } from '../types/Queries';
import { baseQuery as Queries } from './baseQuery';

export const queryApi = createApi({
  baseQuery: Queries,
  reducerPath: 'queryApi',
  endpoints: builder => ({
    createQuery: builder.mutation<
      ISuccessfulResponse | HTTPValidationError,
      {
        site_id: string;
        study_id: string;
        country: string;
        application: string | undefined;
        data: any;
        application_section: string;
        application_module_section: string;
      }
    >({
      query: ({ site_id, study_id, country, application, data, application_section, application_module_section }) => ({
        url: formatString(
          endPoints.Queries.create,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.country, value: country },
          { key: urlKeys.application, value: application },
          { key: urlKeys.application_section, value: application_section },
          { key: urlKeys.application_module_section, value: application_module_section }
        ),
        method: 'POST',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    getQueries: builder.query<
      ICreateQuery,
      {
        site_id: string;
        study_id: string;
        country: string;
        application: string | undefined;
        application_section: string;
        application_module_section: string;
      }
    >({
      query: ({ site_id, study_id, country, application, application_section, application_module_section }) => ({
        url: formatString(
          endPoints.Queries.select,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.country, value: country },
          { key: urlKeys.application, value: application },
          { key: urlKeys.application_section, value: application_section },
          { key: urlKeys.application_module_section, value: application_module_section }
        ),
        method: 'GET'
      }),
      extraOptions: { maxRetries: 3 }
    }),
    updateQuery: builder.mutation<
      ISuccessfulResponse | HTTPValidationError,
      {
        site_id: string;
        study_id: string;
        country: string;
        application: string | undefined;
        data: any;
        application_section: string;
        application_module_section: string;
      }
    >({
      query: ({ site_id, study_id, country, application, data, application_section, application_module_section }) => ({
        url: formatString(
          endPoints.Queries.update,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.country, value: country },
          { key: urlKeys.application, value: application },
          { key: urlKeys.application_section, value: application_section },
          { key: urlKeys.application_module_section, value: application_module_section }
        ),
        method: 'PUT',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    deleteQuery: builder.mutation<
      ISuccessfulResponse | HTTPValidationError,
      {
        site_id: string;
        study_id: string;
        country: string;
        application: string | undefined;
        data: any;
        application_section: string;
        application_module_section: string;
      }
    >({
      query: ({ site_id, study_id, country, application, data, application_section, application_module_section }) => ({
        url: formatString(
          endPoints.Queries.delete,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.country, value: country },
          { key: urlKeys.application, value: application },
          { key: urlKeys.application_section, value: application_section },
          { key: urlKeys.application_module_section, value: application_module_section }
        ),
        method: 'PATCH',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    getAudit: builder.query<
      any,
      {
        site_id: string;
        study_id: string;
        country: string;
        application: string | undefined;
        application_section: string;
        application_module_section: string;
        id: string;
        only_update: any;
      }
    >({
      query: ({
        site_id,
        study_id,
        country,
        application,
        application_section,
        application_module_section,
        id,
        only_update = false
      }) => ({
        url: formatString(
          endPoints.Audit.getAll,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.country, value: country },
          { key: urlKeys.application, value: application },
          { key: urlKeys.application_section, value: application_section },
          { key: urlKeys.application_module_section, value: application_module_section },
          { key: urlKeys.id, value: id },
          { key: urlKeys.only_update, value: only_update }
        ),
        method: 'GET'
      }),
      extraOptions: { maxRetries: 3 }
    }),
    applyFilterGetAllAudit: builder.mutation<any, any>({
      query: ({
        audit_module_section,
        id,
        site_id,
        study_id,
        country,
        application,
        isOnlyUpdate,
        startFrom,
        maxSize,
        sortbyColumn,
        sortOrder,
        data,
        application_section
      }) => ({
        url: `${formatString(
          endPoints.Audit.applyFilterGetAll,
          { key: urlKeys.audit_module_section, value: audit_module_section },
          { key: urlKeys.id, value: id }
        )}?study_id=${study_id}&country=${country}&site_id=${site_id}&application=${application}&only_update=${isOnlyUpdate}&from=${startFrom}&size=${maxSize}&sort_by=${sortbyColumn}&sort_order=${sortOrder}&application_section=${application_section}`,
        method: 'POST',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    forwardQuery: builder.mutation<
      ISuccessfulResponse | HTTPValidationError,
      {
        site_id: string;
        study_id: string;
        country: string;
        application: string | undefined;
        data: any;
        application_section: string;
        application_module_section: string;
      }
    >({
      query: ({ site_id, study_id, country, application, data, application_section, application_module_section }) => ({
        url: formatString(
          endPoints.Queries.forward,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.country, value: country },
          { key: urlKeys.application, value: application },
          { key: urlKeys.application_section, value: application_section },
          { key: urlKeys.application_module_section, value: application_module_section }
        ),
        method: 'PUT',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    })
  })
});

export const {
  useCreateQueryMutation,
  useDeleteQueryMutation,
  useGetQueriesQuery,
  useUpdateQueryMutation,
  useGetAuditQuery,
  useApplyFilterGetAllAuditMutation,
  useForwardQueryMutation
} = queryApi;
